/* City Church shop — theme toggle, announcement marquee, homepage hero and support form.
   Plain script, no dependencies; every part is optional and finds its own markup. */
(() => {
  const STORAGE_KEY = 'cc-theme';
  const root = document.documentElement;
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- Theme (light / dark) ---------------- */

  const savedTheme = () => {
    try { return localStorage.getItem(STORAGE_KEY); } catch (e) { return null; }
  };

  const syncToggles = () => {
    const dark = root.getAttribute('data-theme') === 'dark';
    document.querySelectorAll('[data-cc-theme-toggle]').forEach((btn) => {
      btn.setAttribute('aria-pressed', dark ? 'true' : 'false');
      const label = dark ? btn.dataset.labelToLight : btn.dataset.labelToDark;
      if (label) btn.setAttribute('aria-label', label);
    });
  };

  const applyTheme = (theme) => {
    root.setAttribute('data-theme', theme);
    syncToggles();
  };

  const switchTheme = (btn) => {
    const next = root.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
    try { localStorage.setItem(STORAGE_KEY, next); } catch (e) { /* private mode: session only */ }

    if (reduceMotion()) return applyTheme(next);

    if (document.startViewTransition && btn) {
      // Circular reveal growing out of the toggle.
      const r = btn.getBoundingClientRect();
      const x = r.left + r.width / 2;
      const y = r.top + r.height / 2;
      const radius = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y));
      root.style.setProperty('--cc-vt-x', x + 'px');
      root.style.setProperty('--cc-vt-y', y + 'px');
      root.style.setProperty('--cc-vt-r', radius + 'px');
      document.startViewTransition(() => applyTheme(next));
      return;
    }

    root.classList.add('cc-theme-fading');
    applyTheme(next);
    setTimeout(() => root.classList.remove('cc-theme-fading'), 350);
  };

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-cc-theme-toggle]');
    if (btn) switchTheme(btn);
  });

  // Follow the system setting until the visitor picks a theme themselves.
  const systemDark = window.matchMedia('(prefers-color-scheme: dark)');
  const onSystemChange = (e) => { if (!savedTheme()) applyTheme(e.matches ? 'dark' : 'light'); };
  if (systemDark.addEventListener) systemDark.addEventListener('change', onSystemChange);

  /* ---------------- Announcement marquee ---------------- */

  const initMarquee = () => {
    document.querySelectorAll('#header-group .announcement-bar__slides').forEach((slides) => {
      if (slides.dataset.ccMarquee) return;
      const items = slides.querySelectorAll('.announcement-bar__slide');
      if (items.length !== 1) return; // several announcements keep Horizon's rotator
      const text = items[0].querySelector('.announcement-bar__text');
      if (!text) return;
      slides.dataset.ccMarquee = '1';

      const marquee = document.createElement('div');
      marquee.className = 'cc-marquee';
      const track = document.createElement('div');
      track.className = 'cc-marquee__track';
      marquee.appendChild(track);

      items[0].style.display = 'none';
      slides.appendChild(marquee);
      // Enough copies to cover the bar, doubled: the track slides by exactly half its
      // width, so the loop is seamless. Only the first copy is exposed to assistive tech.
      const probe = text.cloneNode(true);
      track.appendChild(probe);
      const perHalf = Math.max(2, Math.ceil(slides.clientWidth / Math.max(probe.offsetWidth, 1)) + 1);
      probe.remove();
      for (let i = 0; i < perHalf * 2; i++) {
        const copy = text.cloneNode(true);
        if (i > 0) copy.setAttribute('aria-hidden', 'true');
        track.appendChild(copy);
      }
      track.style.animationDuration = Math.max(20, track.scrollWidth / 2 / 40) + 's';
    });
  };

  /* ---------------- Hero slider ---------------- */

  const initHero = (hero) => {
    if (hero.dataset.ready) return;
    hero.dataset.ready = '1';
    const slides = Array.from(hero.querySelectorAll('[data-cch-slide]'));
    const bars = Array.from(hero.querySelectorAll('[data-cch-bar]'));
    const count = hero.querySelector('[data-cch-count]');
    const live = hero.querySelector('[data-cch-live]');
    const pauseBtn = hero.querySelector('[data-cch-pause]');
    if (slides.length === 0) return;

    let index = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')));
    const pad = (n) => (n < 10 ? '0' + n : String(n));

    const show = (i, announce) => {
      index = (i + slides.length) % slides.length;
      slides.forEach((s, n) => {
        const on = n === index;
        s.classList.toggle('is-active', on);
        s.setAttribute('aria-hidden', on ? 'false' : 'true');
        s.inert = !on;
      });
      bars.forEach((b, n) => {
        b.classList.toggle('is-done', n < index);
        b.setAttribute('aria-current', n === index ? 'true' : 'false');
      });
      if (count) count.textContent = pad(index + 1) + ' / ' + pad(slides.length);
      if (announce && live) live.textContent = slides[index].getAttribute('aria-label') || '';
    };

    const setPaused = (paused) => {
      hero.classList.toggle('is-paused', paused);
      if (pauseBtn) {
        pauseBtn.setAttribute('aria-label', paused ? pauseBtn.dataset.labelPlay : pauseBtn.dataset.labelPause);
        pauseBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      }
    };

    // The active bar's fill animation is the timer: it pauses with the slider (CSS),
    // and when it finishes we move on. Reduced motion has no animation, so no autoplay.
    bars.forEach((b) => {
      const fill = b.querySelector('span');
      if (fill) fill.addEventListener('animationend', () => {
        if (b.getAttribute('aria-current') === 'true' && !hero.classList.contains('is-paused')) show(index + 1, false);
      });
    });

    hero.addEventListener('click', (event) => {
      const t = event.target.closest('[data-cch-prev], [data-cch-next], [data-cch-bar], [data-cch-pause]');
      if (!t) return;
      if (t.hasAttribute('data-cch-prev')) show(index - 1, true);
      else if (t.hasAttribute('data-cch-next')) show(index + 1, true);
      else if (t.hasAttribute('data-cch-bar')) show(bars.indexOf(t), true);
      else setPaused(!hero.classList.contains('is-paused'));
    });

    // Hold while the pointer or keyboard focus is inside.
    const hold = (on) => hero.classList.toggle('is-holding', on);
    hero.addEventListener('mouseenter', () => hold(true));
    hero.addEventListener('mouseleave', () => hold(false));
    hero.addEventListener('focusin', () => hold(true));
    hero.addEventListener('focusout', (e) => { if (!hero.contains(e.relatedTarget)) hold(false); });

    if (reduceMotion() || slides.length < 2) setPaused(true);
    show(index, false);
  };

  /* ---------------- Support form ---------------- */

  const fmt = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  const initSupport = (form) => {
    if (form.dataset.ready) return;
    form.dataset.ready = '1';
    let variants = [];
    try { variants = JSON.parse(form.querySelector('[data-cch-variants]').textContent); } catch (e) { return; }
    const presets = Array.from(form.querySelectorAll('[data-cch-preset]'));
    const input = form.querySelector('[data-cch-custom]');
    const customWrap = form.querySelector('[data-cch-custom-wrap]');
    const submit = form.querySelector('[data-cch-submit]');
    const label = form.querySelector('[data-cch-submit-label]');
    const error = form.querySelector('[data-cch-error]');
    const min = parseInt(form.dataset.min || '100', 10);
    const step = parseInt(form.dataset.step || '100', 10);
    const stepVariant = variants.find((v) => v.available && v.price === step * 100);
    let selected = presets.findIndex((p) => p.getAttribute('aria-pressed') === 'true');

    const current = () => {
      const raw = input ? input.value.replace(/\D/g, '') : '';
      if (raw) return { amount: parseInt(raw, 10), custom: true };
      const p = presets[selected];
      return { amount: p ? parseInt(p.dataset.amount, 10) : 0, custom: false };
    };

    // Exact variant for the amount if there is one, else N × the step variant.
    const lineFor = (amount) => {
      const exact = variants.find((v) => v.available && v.price === amount * 100);
      if (exact) return { id: exact.id, quantity: 1 };
      if (stepVariant && amount % step === 0) return { id: stepVariant.id, quantity: amount / step };
      return null;
    };

    const refresh = () => {
      const { amount, custom } = current();
      presets.forEach((p, i) => p.setAttribute('aria-pressed', !custom && i === selected ? 'true' : 'false'));
      if (customWrap) customWrap.classList.toggle('is-active', custom);
      let message = '';
      if (custom && amount < min) message = form.dataset.msgMin || '';
      else if (custom && amount % step !== 0) message = form.dataset.msgStep || '';
      else if (custom && !lineFor(amount)) message = form.dataset.msgUnavailable || '';
      if (error) error.textContent = message;
      submit.disabled = !!message || amount <= 0;
      if (label) label.textContent = (form.dataset.cta || '') + (amount > 0 ? ' · ' + fmt(amount) + ' kr' : '');
    };

    presets.forEach((p, i) => p.addEventListener('click', () => {
      selected = i;
      if (input) input.value = '';
      refresh();
    }));

    if (input) input.addEventListener('input', () => {
      const digits = input.value.replace(/\D/g, '').replace(/^0+/, '').slice(0, 5);
      input.value = digits ? fmt(parseInt(digits, 10)) : '';
      refresh();
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const { amount } = current();
      const line = lineFor(amount);
      if (!line) return refresh();
      submit.disabled = true;
      fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [line] })
      })
        .then((r) => { if (!r.ok) throw new Error('cart'); window.location.href = form.dataset.cartUrl || '/cart'; })
        .catch(() => { if (error) error.textContent = form.dataset.msgError || ''; submit.disabled = false; });
    });

    // Amount chips elsewhere (the hero's support slide) preselect here and scroll to the form.
    form.cchSelect = (amount) => {
      const i = presets.findIndex((p) => parseInt(p.dataset.amount, 10) === amount);
      if (i >= 0) { selected = i; if (input) input.value = ''; }
      else if (input) { input.value = fmt(amount); }
      refresh();
    };

    refresh();
  };

  document.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-cch-pick-amount]');
    if (!chip) return;
    const form = document.querySelector('[data-cch-support]');
    if (!form || !form.cchSelect) return;
    event.preventDefault();
    form.cchSelect(parseInt(chip.dataset.cchPickAmount, 10));
    form.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
    const pressed = form.querySelector('[data-cch-preset][aria-pressed="true"], [data-cch-custom]');
    if (pressed) pressed.focus({ preventScroll: true });
  });

  /* ---------------- Boot ---------------- */

  const boot = () => {
    syncToggles();
    initMarquee();
    document.querySelectorAll('[data-cch-hero]').forEach(initHero);
    document.querySelectorAll('[data-cch-support]').forEach(initSupport);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();

// Print picker: explain a missing print choice in our words, and keep the chosen print
// when Horizon re-renders the buy buttons after a variant change.
(function () {
  var chosen = {};
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input.closest) return;
    var box = input.closest('[data-cc-print]');
    if (!box || input.type !== 'radio') return;
    chosen[box.dataset.productId] = input.value;
    var err = box.querySelector('[data-cc-print-error]');
    if (err) err.hidden = true;
  });
  document.addEventListener('invalid', function (e) {
    var box = e.target.closest && e.target.closest('[data-cc-print]');
    if (!box) return;
    var err = box.querySelector('[data-cc-print-error]');
    if (err) err.hidden = false;
  }, true);
  function restore() {
    document.querySelectorAll('[data-cc-print]').forEach(function (box) {
      var value = chosen[box.dataset.productId];
      if (!value) return;
      box.querySelectorAll('input[type="radio"]').forEach(function (input) {
        if (input.value === value && !input.checked) input.checked = true;
      });
    });
  }
  new MutationObserver(restore).observe(document.documentElement, { childList: true, subtree: true });
})();
